import { createContext, useContext, type ReactElement, type ReactNode } from "react";

/**
 * Whether the surrounding subtree lays its sections out as a reorderable
 * (drag-and-drop) collection. Default `false` — a panel is static unless a
 * provider says otherwise.
 */
const SectionReorderContext = createContext(false);

interface SectionReorderProviderProps {
    children: ReactNode;
    /** Set false to switch the affordance off for a subtree (e.g. read-only). */
    reorderable?: boolean;
}

/**
 * Marks its subtree as drag-reorderable so every {@link SectionPanel} inside it
 * grows a hover affordance without the pages in between having to thread a prop
 * through each card component.
 *
 * Wrap the drag container (the dnd-kit `SortableContext` body), not the
 * individual card — the point is that a card stays unaware of how the page
 * lays it out.
 */
export function SectionReorderProvider({
    children,
    reorderable = true,
}: SectionReorderProviderProps): ReactElement {
    return (
        <SectionReorderContext.Provider value={reorderable}>
            {children}
        </SectionReorderContext.Provider>
    );
}

/** Is the panel being rendered inside a reorderable collection? */
export function useSectionReorderable(): boolean {
    return useContext(SectionReorderContext);
}
